  // assets/AllCourses/Exercises/MissingAlphabetsExercise.jsx
  "use client";

  import React, { useEffect, useRef, useState } from "react";
  import { Button } from "@/components/ui/button";
  import {
    Card,
    CardContent,
    CardHeader,
    CardDescription,
    CardTitle,
  } from "@/components/ui/card";
  import { Input } from "@/components/ui/input";
  import { StringField } from "@/components/shared/FormFields";
  import CapitalizationRule from "@/components/shared/CapitalizationRule";
  import { cn } from "@/utils/utils";

  export default function MissingAlphabetsExercise({
    exercise = {},
    onUpdate,
    errors = {},
    isPreview = false,
    showResults = false,
    onComplete = () => {},
    setAttempt = () => {},
  }) {
    const isEditMode = typeof onUpdate === "function";

    /* ================= SAFE QUESTIONS ================= */
    const questions = Array.isArray(exercise.missingAlphabetsQuestions)
      ? exercise.missingAlphabetsQuestions
      : [];

    /* ================= STUDENT STATE ================= */
    const [state, setState] = useState({
      currentIndex: 0,
      answers: [],
      hasInteracted: false,
    });

    const completedRef = useRef(false);
  useEffect(() => {
    if (!showResults) completedRef.current = false;
  }, [showResults]);

    /* ================= INIT / RESTORE ================= */
    useEffect(() => {
      if (!isPreview) return;

      const initialAnswers = questions.map(q =>
        Array((q.word.match(/_+/g) || []).length).fill("")
      );

      if (exercise.attempt?.answers) {
        setState({
          currentIndex: exercise.attempt.currentIndex || 0,
          answers: exercise.attempt.answers,
          hasInteracted: !!exercise.attempt.hasInteracted,
        });
      } else {
        setState({
          currentIndex: 0,
          answers: initialAnswers,
          hasInteracted: false,
        });
      }
    }, [isPreview, questions, exercise.attempt]);

    useEffect(() => {
      if (isPreview) completedRef.current = false;
    }, [exercise._id, isPreview]);

    /* ================= NORMALIZE ================= */
    const normalize = (text = "", requireCap = false) => {
      let t = String(text).trim();
      if (!requireCap) t = t.toLowerCase();
      return t.replace(/[.,!?]/g, "");
    };

    /* ================= EDIT HELPERS ================= */
    const updateQuestions = next => {
      if (isEditMode) onUpdate("missingAlphabetsQuestions", next);
    };

    const updateWord = (idx, value) => {
      if (!isEditMode) return;

      const next = [...questions];
      const blanks = (value.match(/_+/g) || []).length;
      const prevAnswers = next[idx]?.answer || [];

      next[idx] = {
        ...next[idx],
        word: value,
        answer: Array.from({ length: blanks }, (_, i) => prevAnswers[i] || ""),
      };

      updateQuestions(next);
    };

    /* ================= STUDENT INPUT ================= */
    const handleInputChange = (blankIdx, value) => {
      if (isEditMode || showResults) return;

      setState(prev => {
        const next = structuredClone(prev);
        next.answers[next.currentIndex][blankIdx] = value;
        next.hasInteracted = true;

        setAttempt(next);

        const allFilled = next.answers.every(a =>
          a.every(v => v.trim() !== "")
        );

        if (allFilled && !completedRef.current) {
          completedRef.current = true;
          checkAnswers(next);
        }

        return next;
      });
    };

    const nextQuestion = () => {
      setState(prev => {
        const next = { ...prev, currentIndex: prev.currentIndex + 1 };
        setAttempt(next);
        return next;
      });
    };

    const checkAnswers = currentState => {
      const allCorrect = questions.every((q, qIdx) => {
        const blanks = (q.word.match(/_+/g) || []).length;

        return Array.from({ length: blanks }).every((_, aIdx) => {
          const user = currentState.answers[qIdx]?.[aIdx] || "";
          const correct = q.answer[aIdx] || "";

          return normalize(user, q.requireCapitalization) ===
                normalize(correct, q.requireCapitalization);
        });
      });

      onComplete(exercise._id, allCorrect);
    };

    /* ================= EDIT MODE ================= */
    if (isEditMode) {
      return (
        <div className="space-y-8">
          {questions.map((q, idx) => {
            const blanks = (q.word.match(/_+/g) || []).length;

            return (
              <Card key={idx} className="p-6">
                <div className="space-y-6">
                  <StringField
                    label={`Word ${idx + 1} (use _ for missing letters)`}
                    value={q.word || ""}
                    onChange={v => updateWord(idx, v)}
                    placeholder="A__le"
                    required
                    error={errors?.[`exercise-${exercise.index}-ma-word-${idx}`]}
                  />

                  <div className="space-y-3">
                    <p className="font-medium">Missing Letters</p>

                    {blanks === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        Add underscores (_) to create blanks
                      </p>
                    ) : (
                      Array.from({ length: blanks }).map((_, aIdx) => (
                        <StringField
                          key={aIdx}
                          label={`Letter ${aIdx + 1}`}
                          value={q.answer[aIdx] || ""}
                          onChange={v => {
                            const next = [...questions];
                            next[idx].answer[aIdx] = v;
                            updateQuestions(next);
                          }}
                          maxLength={1}
                          placeholder="p"
                          required
                        />
                      ))
                    )}
                  </div>

                  <CapitalizationRule
                    checked={!!q.requireCapitalization}
                    onChange={c => {
                      const next = [...questions];
                      next[idx].requireCapitalization = c;
                      updateQuestions(next);
                    }}
                  />

                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() =>
                      updateQuestions(
                        questions.filter((_, i) => i !== idx)
                      )
                    }
                  >
                    Remove Word
                  </Button>
                </div>
              </Card>
            );
          })}

          <Button
            variant="outline"
            onClick={() =>
              updateQuestions([
                ...questions,
                { word: "", answer: [], requireCapitalization: false },
              ])
            }
          >
            Add Word
          </Button>
        </div>
      );
    }

    /* ================= STUDENT MODE ================= */
    const current = questions[state.currentIndex];
    if (!current) {
      return (
        <Card>
          <CardHeader>
            <CardDescription>
              No missing alphabet questions available.
            </CardDescription>
          </CardHeader>
        </Card>
      );
    }

    const parts = current.word.split(/_+/g);
    let blankIdx = 0;

    return (
      <Card className="max-w-3xl mx-auto shadow-sm">
        <CardHeader>
          <CardTitle className="text-base">
            Missing Letters
          </CardTitle>
          <CardDescription>
            Question {state.currentIndex + 1} of {questions.length}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-8">
          <div className="rounded-xl border bg-muted/40 p-8 text-center">
            <div className="flex flex-wrap justify-center items-center gap-3 text-4xl font-bold tracking-wide">
            {parts.map((part, i) => {
                if (i === parts.length - 1) {
                  return (
                    <React.Fragment key={i}>
                      <span>{part}</span>
                    </React.Fragment>
                  );
                }

                const currentBlankIndex = blankIdx;
                blankIdx++;

                const userValue =
                  state.answers[state.currentIndex]?.[currentBlankIndex] || "";

                const correctValue =
                  current.answer[currentBlankIndex] || "";

                const isCorrect =
                  userValue &&
                  normalize(userValue, current.requireCapitalization) ===
                    normalize(correctValue, current.requireCapitalization);

                return (
                  <React.Fragment key={i}>
                    <span>{part}</span>
                    <Input
                      maxLength={1}
                      value={userValue}
                      onChange={e =>
                        handleInputChange(currentBlankIndex, e.target.value)
                      }
                      disabled={showResults}
                      className={cn(
                        "w-14 h-14 text-center text-3xl font-bold transition border",

                        // CORRECT
                        showResults && isCorrect &&
                          "bg-green-100 border-green-300",

                        // WRONG (only if typed)
                        showResults && userValue && !isCorrect &&
                          "bg-red-100 border-red-300",

                        showResults && "cursor-not-allowed"                   
                      )}
                    />
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {!showResults &&
            state.currentIndex < questions.length - 1 && (
              <div className="text-center">
                <Button
                  size="lg"
                  onClick={nextQuestion}
                  disabled={state.answers[state.currentIndex]?.some(a => !a.trim())}
                >
                  Next →
                </Button>
              </div>
            )}
        </CardContent>
      </Card>
    );
  }
